import { EmptyState } from "@amber/ui";
import type { EvidenceRecord, TurnView } from "../../contracts/index";
import type { Editing } from "../editing/Editing";
import { DocumentFacts, DocumentView } from "./DocumentView";
import { KeyFacts, MediaPlayer, SpeakerTracks, type PlayerState } from "./Player";
import { TimingMode } from "./Timing";

interface PlayerSectionProps {
  record: EvidenceRecord;
  turns: readonly TurnView[];
  player: PlayerState;
  editing: Editing;
  selectedTurn: number | null;
  setSelectedTurn: (idx: number | null) => void;
  timing: boolean;
  setTiming: (on: boolean) => void;
}

export function PlayerSection(props: PlayerSectionProps) {
  return (
    <section
      aria-label={props.record.document ? "Archived document" : "Player"}
      className="flex min-h-0 flex-col overflow-y-auto rounded-xl border border-divider bg-bg max-compact:flex-none max-compact:overflow-visible"
    >
      <PlayerBody {...props} />
    </section>
  );
}

function PlayerBody(props: PlayerSectionProps) {
  const { record, turns, player, selectedTurn, setSelectedTurn } = props;
  if (!record.media && !record.transcript) {
    return (
      <EmptyState title="Nothing was archived" className="my-auto">
        Nothing was archived, so there is no media or transcript yet.{" "}
        <a
          href={record.provenance.original.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-link underline"
        >
          Open the original
        </a>
      </EmptyState>
    );
  }
  if (record.document) {
    return (
      <>
        <DocumentView record={record} turns={turns} selected={selectedTurn} onSelect={setSelectedTurn} />
        <DocumentFacts record={record} />
      </>
    );
  }
  return (
    <>
      <MediaPlayer record={record} player={player} turns={turns} speakers={record.speakers} />
      <MediaTracks {...props} />
      <KeyFacts record={record} />
    </>
  );
}

function MediaTracks({
  record,
  turns,
  player,
  editing,
  selectedTurn,
  setSelectedTurn,
  timing,
  setTiming,
}: PlayerSectionProps) {
  if (!record.transcript) {
    return (
      <p className="m-0 border-t border-divider px-3 py-3 text-md text-muted">
        Speaker tracks appear after transcription.
      </p>
    );
  }
  if (timing) {
    return (
      <TimingMode
        record={record}
        player={player}
        editing={editing}
        selected={selectedTurn}
        onSelect={setSelectedTurn}
        onExit={() => {
          setTiming(false);
        }}
      />
    );
  }
  return (
    <SpeakerTracks
      record={record}
      player={player}
      turns={turns}
      speakers={record.speakers}
      selected={selectedTurn}
      onSelect={setSelectedTurn}
      onAdjustTiming={
        record.actions.timing.enabled && !record.pinned
          ? () => {
              setTiming(true);
            }
          : null
      }
    />
  );
}
